import React from 'react';

import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';

import { type VisibilityConfigInput } from '~/api/graphql';
import { useOptionalCurrentAgencyUser } from '~/components/current-user';
import { useDefaultVisibilityValue } from './default-visibility';
import { VisibilitySelector } from './visibility-selector';
import {
  visibilityInputFromValue,
  type VisibilitySelectorValue
} from './visibility-selector.utils';
import { useVisibilityEntitlement } from './use-visibility-entitlement';

interface BulkVisibilityDialogProps {
  open: boolean;
  count: number;
  itemLabel: string;
  notice: React.ReactNode;
  resultMessage?: React.ReactNode;
  errorMessage?: React.ReactNode;
  loading?: boolean;
  onClose: () => void;
  onSave: (visibility: VisibilityConfigInput) => Promise<void> | void;
}

export const BulkVisibilityDialog: React.FC<BulkVisibilityDialogProps> = ({
  open,
  count,
  itemLabel,
  notice,
  resultMessage,
  errorMessage,
  loading = false,
  onClose,
  onSave
}) => {
  const visibilityEnabled = useVisibilityEntitlement();
  const defaultVisibility = useDefaultVisibilityValue();
  const [draft, setDraft] = React.useState<VisibilitySelectorValue>(defaultVisibility);
  const disallowPrivate =
    useOptionalCurrentAgencyUser()?.agency?.configuration?.visibility?.disallowPrivate ?? false;

  React.useEffect(() => {
    if (open) {
      setDraft(defaultVisibility);
    }
  }, [defaultVisibility, open]);

  if (!visibilityEnabled) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Change visibility</DialogTitle>
      <DialogDescription>
        Choose the visibility to apply to <strong>{count}</strong>{' '}
        {count === 1 ? itemLabel : `${itemLabel}s`}.
      </DialogDescription>
      <DialogBody>
        <VisibilitySelector value={draft} onChange={setDraft} disallowPrivate={disallowPrivate} />
        <p className="mt-4 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
          {notice}
        </p>
        {resultMessage && (
          <p className="mt-3 rounded-md border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 px-3 py-2 text-sm text-sky-900 dark:text-sky-200">
            {resultMessage}
          </p>
        )}
        {errorMessage && (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            {errorMessage}
          </p>
        )}
      </DialogBody>
      <DialogActions>
        <Button type="button" color="white" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="button"
          color="sky"
          loading={loading}
          onClick={() => onSave(visibilityInputFromValue(draft))}
        >
          Update visibility
        </Button>
      </DialogActions>
    </Dialog>
  );
};
